import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle2,
  Loader2,
  MessageSquare,
  MoreVertical,
  PlayCircle,
  RefreshCw,
  Reply,
  Search,
  X,
} from 'lucide-react';
import {
  fetchVendorContactQueries,
  replyVendorContactQuery,
  updateVendorContactQueryStatus,
  type VendorContactQueryRow,
  type VendorContactQueryStatus,
} from '../../services/MasterAdmin/vendorContactQueriesService';
import { fetchVendors, processVendorsForDisplay } from '../../services/MasterAdmin/vendorService';

const SERVICE_TYPE_OPTIONS = [
  'GENERAL',
  'MEDICINE',
  'AMBULANCE',
  'DOCTOR_INSTANT',
  'DOCTOR_SCHEDULED',
  'LAB_TEST',
  'BLOOD_BANK',
  'HOSPITAL',
  'PRODUCT',
];

const SERVICE_TYPE_LABELS: Record<string, string> = {
  GENERAL: 'General',
  MEDICINE: 'Medicine',
  AMBULANCE: 'Ambulance',
  DOCTOR_INSTANT: 'Doctor (Instant)',
  DOCTOR_SCHEDULED: 'Doctor (Scheduled)',
  LAB_TEST: 'Lab Test',
  BLOOD_BANK: 'Blood Bank',
  HOSPITAL: 'Hospital',
  PRODUCT: 'Product',
};

const statusBadgeClass = (status: VendorContactQueryStatus) => {
  switch (status) {
    case 'OPEN':
      return 'bg-amber-100 text-amber-800 ring-1 ring-amber-200/80 dark:bg-amber-900/35 dark:text-amber-200 dark:ring-amber-800/50';
    case 'IN_PROGRESS':
      return 'bg-blue-100 text-blue-800 ring-1 ring-blue-200/80 dark:bg-blue-900/35 dark:text-blue-200 dark:ring-blue-800/50';
    case 'REPLIED':
      return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200/80 dark:bg-emerald-900/35 dark:text-emerald-200 dark:ring-emerald-800/50';
    default:
      return 'bg-gray-100 text-gray-700 ring-1 ring-gray-200/80 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700';
  }
};

const formatServiceType = (value: string) =>
  SERVICE_TYPE_LABELS[value] || value.replace(/_/g, ' ');

const VendorContactQueriesPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<VendorContactQueryRow[]>([]);
  const [status, setStatus] = useState<string>('');
  const [serviceType, setServiceType] = useState<string>('');
  const [search, setSearch] = useState('');
  const [vendorNamesById, setVendorNamesById] = useState<Record<string, string>>({});
  const [actionId, setActionId] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<VendorContactQueryRow | null>(null);
  const [maReply, setMaReply] = useState('');
  const [actionMenu, setActionMenu] = useState<{
    queryId: string;
    top: number;
    right: number;
  } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchVendorContactQueries({
        ...(status ? { status: status as VendorContactQueryStatus } : {}),
        ...(serviceType ? { serviceType } : {}),
        limit: 200,
        offset: 0,
      });
      setRows(response.data || []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err?.response?.data?.message || 'Failed to load vendor contact queries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, serviceType]);

  useEffect(() => {
    const loadVendorNames = async () => {
      try {
        const response = await fetchVendors();
        const vendors = processVendorsForDisplay(response);
        const map: Record<string, string> = {};
        vendors.forEach((vendor) => {
          map[vendor.id] = vendor.name;
        });
        setVendorNamesById(map);
      } catch {
        // Non-blocking
      }
    };
    void loadVendorNames();
  }, []);

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (actionMenuRef.current?.contains(t)) return;
      const trigger = (e.target as HTMLElement | null)?.closest('[data-contact-query-actions]');
      if (trigger?.getAttribute('data-contact-query-actions') === actionMenu.queryId) return;
      close();
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [actionMenu]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => {
      const haystack = [
        vendorNamesById[row.vendorId],
        row.vendorId,
        row.serviceType,
        row.querySubject,
        row.queryDescription,
        row.maReply,
        row.vendorSnapshot?.email,
        row.vendorSnapshot?.phoneNumber,
        row.vendorSnapshot?.generatedId,
        row.vendorSnapshot?.vendorRoleName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [rows, search, vendorNamesById]);

  const actionMenuRow = useMemo(
    () => (actionMenu ? filteredRows.find((r) => r.queryId === actionMenu.queryId) : null),
    [actionMenu, filteredRows]
  );

  const serviceProviderName = (row: VendorContactQueryRow) =>
    vendorNamesById[row.vendorId] ||
    row.vendorSnapshot?.generatedId ||
    row.vendorSnapshot?.email ||
    'Unknown provider';

  const handleMarkInProgress = async (row: VendorContactQueryRow) => {
    setActionMenu(null);
    try {
      setActionId(row.queryId);
      await updateVendorContactQueryStatus(row.queryId, 'IN_PROGRESS');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err?.response?.data?.message || 'Failed to update status');
    } finally {
      setActionId(null);
    }
  };

  const handleClose = async (row: VendorContactQueryRow) => {
    setActionMenu(null);
    const confirmed = window.confirm(`Close query "${row.querySubject}"?`);
    if (!confirmed) return;
    try {
      setActionId(row.queryId);
      await updateVendorContactQueryStatus(row.queryId, 'CLOSED');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err?.response?.data?.message || 'Failed to close query');
    } finally {
      setActionId(null);
    }
  };

  const openReply = (row: VendorContactQueryRow) => {
    setActionMenu(null);
    setReplyTarget(row);
    setMaReply(row.maReply || '');
    setError(null);
  };

  const handleSendReply = async () => {
    if (!replyTarget) return;
    const text = maReply.trim();
    if (!text) {
      setError('Reply message is required.');
      return;
    }
    try {
      setActionId(replyTarget.queryId);
      await replyVendorContactQuery(replyTarget.queryId, { maReply: text, status: 'REPLIED' });
      setReplyTarget(null);
      setMaReply('');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err?.response?.data?.message || 'Failed to send reply');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-[88rem] mx-auto space-y-5 p-3 sm:p-4 md:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600/10 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400">
              <MessageSquare className="h-5 w-5" />
            </span>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                Vendor Contact Queries
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Review vendor messages and reply — vendors receive a push notification on reply.
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700/80"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white p-4 shadow-sm dark:border-gray-700/80 dark:bg-gray-800/90">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          <label className="relative lg:col-span-5">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder="Search provider, subject, message, reply..."
              className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none ring-blue-500/0 transition focus:border-blue-300 focus:bg-white focus:ring-2 dark:border-gray-600 dark:bg-gray-900/60 dark:text-white dark:focus:border-blue-500"
            />
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.currentTarget.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white lg:col-span-3"
          >
            <option value="">All statuses</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In progress</option>
            <option value="REPLIED">Replied</option>
            <option value="CLOSED">Closed</option>
          </select>
          <select
            value={serviceType}
            onChange={(e) => setServiceType(e.currentTarget.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white lg:col-span-4"
          >
            <option value="">All service types</option>
            {SERVICE_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {formatServiceType(type)}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {loading ? 'Loading…' : `${filteredRows.length.toLocaleString()} queries`}
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm dark:border-gray-700/80 dark:bg-gray-800/90">
        <div className="ma-table-scroll scrollbar-light">
          <table className="w-full min-w-[64rem] text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/90 dark:border-gray-700 dark:bg-gray-900/50">
                <th className="w-[22%] min-w-[12rem] px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Service provider name
                </th>
                <th className="w-[15%] min-w-[9rem] px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Service type
                </th>
                <th className="w-[28%] min-w-[16rem] px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Query
                </th>
                <th className="w-[25%] min-w-[15rem] px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  MA reply
                </th>
                <th className="w-[10%] min-w-[8rem] px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="w-14 px-2 py-3.5" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="animate-pulse">
                    <td colSpan={6} className="px-4 py-6">
                      <div className="h-4 rounded bg-gray-200 dark:bg-gray-700" />
                    </td>
                  </tr>
                ))}
              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-14 text-center text-gray-500 dark:text-gray-400">
                    No vendor contact queries found.
                  </td>
                </tr>
              )}
              {!loading &&
                filteredRows.map((row) => (
                  <tr
                    key={row.queryId}
                    className="transition-colors hover:bg-gray-50/70 dark:hover:bg-gray-900/25"
                  >
                    <td className="px-4 py-4 align-top">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {serviceProviderName(row)}
                      </p>
                      {row.vendorSnapshot?.vendorRoleName && (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {row.vendorSnapshot.vendorRoleName}
                        </p>
                      )}
                      <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400">
                        {row.vendorSnapshot?.email || '—'}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {row.vendorSnapshot?.phoneNumber || '—'}
                      </p>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <span className="inline-flex rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                        {formatServiceType(row.serviceType)}
                      </span>
                      {row.priority === 'HIGH' && (
                        <span className="ml-1.5 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700 dark:bg-red-950/50 dark:text-red-300">
                          High
                        </span>
                      )}
                    </td>
                    <td className="max-w-xs px-4 py-4 align-top lg:max-w-sm">
                      <p className="font-medium text-gray-900 dark:text-white">{row.querySubject}</p>
                      <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                        {row.queryDescription}
                      </p>
                      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-500">
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                      </p>
                    </td>
                    <td className="max-w-xs px-4 py-4 align-top lg:max-w-sm">
                      {row.maReply ? (
                        <>
                          <p className="line-clamp-4 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                            {row.maReply}
                          </p>
                          <p className="mt-2 text-[11px] text-gray-500">
                            {row.maRepliedByName || 'Master Admin'}
                            {row.maRepliedAt ? ` · ${new Date(row.maRepliedAt).toLocaleString()}` : ''}
                          </p>
                        </>
                      ) : (
                        <span className="text-xs italic text-gray-400">Awaiting reply</span>
                      )}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusBadgeClass(row.status)}`}
                      >
                        {row.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-2 py-4 align-top text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        data-contact-query-actions={row.queryId}
                        aria-label="Row actions"
                        aria-expanded={actionMenu?.queryId === row.queryId}
                        disabled={actionId === row.queryId}
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setActionMenu((cur) =>
                            cur?.queryId === row.queryId
                              ? null
                              : {
                                  queryId: row.queryId,
                                  top: rect.bottom + 4,
                                  right: window.innerWidth - rect.right,
                                }
                          );
                        }}
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700"
                      >
                        {actionId === row.queryId ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <MoreVertical className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {actionMenu &&
        actionMenuRow &&
        createPortal(
          <div
            ref={actionMenuRef}
            className="fixed z-[300] min-w-[210px] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800"
            style={{ top: actionMenu.top, right: actionMenu.right }}
          >
            <ul className="m-0 list-none p-0">
              {actionMenuRow.status !== 'CLOSED' && (
                <>
                  <li>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/80"
                      onClick={() => openReply(actionMenuRow)}
                    >
                      <Reply className="h-4 w-4 opacity-70" />
                      {actionMenuRow.maReply ? 'Edit reply' : 'Reply to vendor'}
                    </button>
                  </li>
                  {actionMenuRow.status === 'OPEN' && (
                    <li>
                      <button
                        type="button"
                        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/80"
                        onClick={() => void handleMarkInProgress(actionMenuRow)}
                      >
                        <PlayCircle className="h-4 w-4 opacity-70" />
                        Mark in progress
                      </button>
                    </li>
                  )}
                  <li>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/80"
                      onClick={() => void handleClose(actionMenuRow)}
                    >
                      <CheckCircle2 className="h-4 w-4 opacity-70" />
                      Close query
                    </button>
                  </li>
                </>
              )}
              {actionMenuRow.status === 'CLOSED' && (
                <li className="px-3.5 py-2.5 text-xs text-gray-500 dark:text-gray-400">No actions</li>
              )}
            </ul>
          </div>,
          document.body
        )}

      {replyTarget &&
        createPortal(
          <div className="fixed inset-0 z-[400] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4">
            <div
              className="w-full max-w-lg rounded-t-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800 sm:rounded-2xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="reply-dialog-title"
            >
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700">
                <div>
                  <h2 id="reply-dialog-title" className="text-lg font-bold text-gray-900 dark:text-white">
                    Reply to vendor
                  </h2>
                  <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                    {serviceProviderName(replyTarget)} · {formatServiceType(replyTarget.serviceType)}
                  </p>
                  <p className="mt-1 text-sm font-medium text-gray-800 dark:text-gray-200">
                    {replyTarget.querySubject}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setReplyTarget(null);
                    setMaReply('');
                  }}
                  className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="px-5 py-4">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Your message
                </label>
                <textarea
                  value={maReply}
                  onChange={(e) => setMaReply(e.currentTarget.value)}
                  rows={6}
                  placeholder="Write your reply — the vendor will receive a push notification."
                  className="w-full resize-y rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setReplyTarget(null);
                    setMaReply('');
                  }}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-gray-600 dark:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleSendReply()}
                  disabled={actionId === replyTarget.queryId}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {actionId === replyTarget.queryId ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Reply className="h-4 w-4" />
                      Send reply & notify
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default VendorContactQueriesPage;
